// 📄 src/elements/animate.ts  (GSAP-only reveals; no animate.css)

import { applyPreset, readNumberAttr } from "@/animate/motionPresets";

// Baseline reveal duration in seconds (matches motion presets' default)
const BASELINE_S = 1.0;

// Keyword presets → absolute seconds (derived from baseline)
const SPEED_PRESETS: Record<string, number> = {
  slower: BASELINE_S * 2.0,  // 2.00s
  slow:   BASELINE_S * 1.4,  // 1.40s
  normal: BASELINE_S * 1.0,  // 1.00s
  fast:   BASELINE_S * 0.7,  // 0.70s
  faster: BASELINE_S * 0.4,  // 0.40s
};

/**
 * Parse `anim-speed`:
 *  - keywords: slower|slow|normal|fast|faster
 *  - seconds: "1.2"
 *  - milliseconds: "800" (>=20 → ms)
 */
function parseAnimSpeed(v?: string | null): number | undefined {
  if (!v) return undefined;
  const t = v.trim().toLowerCase();
  if (!t) return undefined;
  if (t in SPEED_PRESETS) return SPEED_PRESETS[t as keyof typeof SPEED_PRESETS];
  const n = parseFloat(t);
  if (Number.isNaN(n)) return undefined;
  return n > 20 ? n / 1000 : n; // seconds
}

// Single source of truth: what is animatable
const CANDIDATES_SELECTOR = ".sc-anim, [data-anim]";

// Optional convenience: headings/captions often carry [data-anim] anyway,
// but keep them in case authors forget to tag.
const ANIM_EL_SELECTOR =
  `${CANDIDATES_SELECTOR}, h1, h2, h3, p, .lead, hr, .caption, [data-caption], figcaption`;

// Never animate elements inside these containers
const EXCLUDE_ANCESTOR =
  ".hud-reel-controls, .lite-hud-controls, .reel-audio-gate, .pause-overlay, .sc-captions-layer";

// Hide animatable elements until their reveal runs
export function primeHiddenState(section: HTMLElement): void {
  const list = Array.from(section.querySelectorAll<HTMLElement>(CANDIDATES_SELECTOR));
  list.forEach((el) => {
    if (el.closest(EXCLUDE_ANCESTOR)) return;
    if ((el.dataset.animated || "").toLowerCase() === "true") return;
    if (!el.hasAttribute("data-anim")) el.setAttribute("data-anim", "fade-in");
    el.style.visibility = "hidden";
    el.style.opacity = "0";
  });
}

declare const gsap: any;

/** Kill any running/queued tweens for a section (safe if GSAP missing) */
export function cancelSectionAnimations(section: HTMLElement) {
  try {
    const G = (window as any).gsap || (typeof gsap !== "undefined" ? gsap : null);
    if (!G) return;
    section.querySelectorAll<HTMLElement>(CANDIDATES_SELECTOR).forEach((el) => {
      if (!el.closest(EXCLUDE_ANCESTOR)) G.killTweensOf?.(el);
    });
  } catch {}
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  } catch {
    return false;
  }
}

/** GSAP-driven element reveals for a section. Resolves when animations finish. */
export function animateElements(section: HTMLElement): Promise<void> {
  return new Promise<void>((resolve) => {
    section.dataset.revealed = "1";

    // Pre-hide so delayed tweens don't flash
    primeHiddenState(section);

    const speedAttr   = section.getAttribute("anim-speed")?.trim();
    const staggerAttr = section.getAttribute("anim-stagger")?.trim();
    const distAttr    = section.getAttribute("anim-dist")?.trim();

    const customDur = parseAnimSpeed(speedAttr);
    const customStagger =
      staggerAttr && !Number.isNaN(parseFloat(staggerAttr))
        ? (parseFloat(staggerAttr) > 20 ? parseFloat(staggerAttr) / 1000 : parseFloat(staggerAttr))
        : 0;
    const customDist =
      distAttr && !Number.isNaN(parseFloat(distAttr)) ? parseFloat(distAttr) : undefined;

    // Collect only explicit animatables + common text nodes
    const candidates = Array.from(section.querySelectorAll<HTMLElement>(ANIM_EL_SELECTOR));
    const els = candidates
      .filter((el) => !el.closest(EXCLUDE_ANCESTOR))
      .filter((el) => (el.dataset.animated || "").toLowerCase() !== "true");

    // A11y: reduced motion → snap visible
    if (prefersReducedMotion()) {
      els.forEach((el) => {
        el.dataset.animated = "true";
        el.style.opacity = "1";
        el.style.transform = "none";
        el.style.visibility = "visible";
      });
      resolve();
      return;
    }

    if (els.length === 0) { resolve(); return; }

    let idx = 0;
    let longest = 0;

    els.forEach((el) => {
      const token = (el.getAttribute("data-anim") || "").trim().toLowerCase() || "fade-in";

      const perElDelay =
        readNumberAttr(el, "data-anim-delay", idx * (customStagger || 0)) || 0;

      const elDur  = readNumberAttr(el, "data-anim-dur",  customDur ?? 0) || customDur;
      const elDist = readNumberAttr(el, "data-anim-dist", customDist ?? 0) || customDist;

      const finish = perElDelay + (elDur || BASELINE_S);
      if (finish > longest) longest = finish;

      try {
        applyPreset(el, token, { delay: perElDelay, dur: elDur, dist: elDist });
        window.setTimeout(() => {
          el.dataset.animated = "true";
          el.style.visibility = "visible";
        }, Math.max(0, perElDelay) * 1000);
      } catch {
        el.style.opacity = "1";
        el.style.visibility = "visible";
        el.style.transform = "none";
        el.dataset.animated = "true";
      }

      idx += 1;
    });

    setTimeout(() => resolve(), longest * 1000);
  });
}